import { FormEvent, useMemo, useState } from 'react';
import Modal from '../../../../shared/modal';

const validatePassword = (password: string) =>
    password.length >= 6
    && /[A-Z]/.test(password)
    && /[^A-Za-z0-9]/.test(password);

const inputClassName = 'w-full rounded-xl border bg-white dark:bg-black px-3 py-2 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode focus:outline-none focus:border-gray-300 dark:focus:border-gray-600';

const ResetAgentClientPasswordModal = ({
    open,
    username,
    loading,
    error,
    onClose,
    onSubmit,
}: {
    open: boolean;
    username: string;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onSubmit: (password: string) => Promise<boolean>;
}) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [touchedSubmit, setTouchedSubmit] = useState(false);

    const validationErrors = useMemo(() => {
        const errors: { password?: string; confirmPassword?: string; username?: string } = {};
        if (!username.trim() || username === '--') {
            errors.username = 'Username is required to reset password.';
        }
        if (!validatePassword(password)) {
            errors.password = 'Password must be at least 6 characters and include one uppercase letter and one symbol.';
        }
        if (confirmPassword !== password) {
            errors.confirmPassword = 'Passwords do not match.';
        }
        return errors;
    }, [confirmPassword, password, username]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    const resetAndClose = () => {
        if (loading) return;
        setPassword('');
        setConfirmPassword('');
        setTouchedSubmit(false);
        onClose();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setTouchedSubmit(true);
        if (hasValidationErrors || loading) return;

        const updated = await onSubmit(password);
        if (!updated) return;

        setPassword('');
        setConfirmPassword('');
        setTouchedSubmit(false);
    };

    return (
        <Modal
            open={open}
            onClose={resetAndClose}
            title='Reset Client Password'
            contentContainerStyle='w-full max-w-[520px] !max-h-[90vh] overflow-y-auto'
        >
            <form className='pt-2 pb-1' onSubmit={handleSubmit}>
                <div className='space-y-4'>
                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Username
                        </label>
                        <input
                            value={username}
                            disabled
                            className={`${inputClassName} disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-bgColor-darkMode`}
                        />
                        {touchedSubmit && validationErrors.username && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>
                                {validationErrors.username}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            New Password
                        </label>
                        <input
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            placeholder='NewStrongPass1!'
                            type='password'
                            className={inputClassName}
                        />
                        <p className={`mt-1 text-xs ${
                            touchedSubmit && validationErrors.password
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-500 dark:text-gray-400'
                        }`}>
                            At least 6 characters, one uppercase letter and one symbol.
                        </p>
                    </div>

                    <div>
                        <label className='mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300'>
                            Confirm Password
                        </label>
                        <input
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                            placeholder='Confirm new password'
                            type='password'
                            className={inputClassName}
                        />
                        {touchedSubmit && validationErrors.confirmPassword && (
                            <p className='mt-1 text-xs text-red-600 dark:text-red-400'>
                                {validationErrors.confirmPassword}
                            </p>
                        )}
                    </div>
                </div>

                {error && (
                    <p className='mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300'>
                        {error}
                    </p>
                )}

                <div className='mt-6 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-bgColor-darkMode py-3'>
                    <button
                        type='button'
                        onClick={resetAndClose}
                        disabled={loading}
                        className='rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-borderColor-darkMode dark:text-primary-darkMode hover:bg-gray-50 dark:hover:bg-bgColor-darkMode disabled:opacity-50'
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={loading}
                        className='rounded-xl border border-[#00330f] bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:border-white dark:bg-white dark:text-black hover:opacity-90 disabled:opacity-50'
                    >
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ResetAgentClientPasswordModal;
